import React, { type FC, type ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { standardTooltipProps } from './tooltipStyles'

interface LongPressIconButtonProps {
  readonly icon: ReactNode
  readonly onLongPress: () => void
  readonly tooltipText: string
  readonly duration?: number
  readonly progressColor?: string
  readonly color?: string
  readonly activeColor?: string | undefined
  readonly disabled?: boolean
}

export const LongPressIconButton: FC<LongPressIconButtonProps> = ({
  icon,
  onLongPress,
  tooltipText,
  duration = 1000,
  progressColor,
  color,
  activeColor,
  disabled = false,
}) => {
  const [isHolding, setIsHolding] = useState(false)
  const [progress, setProgress] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number>(0)

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const resetState = useCallback(() => {
    clearTimer()
    setIsHolding(false)
    setProgress(0)
    startTimeRef.current = 0
  }, [clearTimer])

  const handleStart = useCallback(() => {
    if (disabled) return

    setIsHolding(true)
    setProgress(0)
    startTimeRef.current = Date.now()

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current
      const newProgress = Math.min((elapsed / duration) * 100, 100)
      setProgress(newProgress)

      if (elapsed >= duration) {
        clearTimer()
        onLongPress()
        setIsHolding(false)
        setProgress(0)
      }
    }, 16)
  }, [disabled, duration, onLongPress, clearTimer])

  const handleEnd = useCallback(() => {
    resetState()
  }, [resetState])

  // Stop click from bubbling to parent toggle handlers — mousedown is intentionally
  // NOT stopped here because it initiates the long-press sequence.
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  useEffect(() => {
    return () => clearTimer()
  }, [clearTimer])

  const remainingPercent = 100 - progress
  const effectiveColor = color ?? 'error.main'
  const effectiveProgressColor = progressColor ?? effectiveColor

  return (
    <Tooltip {...standardTooltipProps} title={tooltipText}>
      <Box sx={containerSx}>
        {isHolding && (
          <Box sx={progressTrackSx}>
            <Box sx={createProgressBarSx(remainingPercent, effectiveProgressColor, 'left')} />
            <Box sx={createProgressBarSx(remainingPercent, effectiveProgressColor, 'right')} />
          </Box>
        )}
        <IconButton
          size="small"
          disabled={disabled}
          onMouseDown={handleStart}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchEnd={handleEnd}
          onClick={handleClick}
          sx={createButtonSx(effectiveColor, activeColor)}
        >
          {icon}
        </IconButton>
      </Box>
    </Tooltip>
  )
}

const containerSx: SxProps<Theme> = {
  position: 'relative',
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
}

const progressTrackSx: SxProps<Theme> = {
  position: 'absolute',
  top: -4,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 24,
  height: 3,
  backgroundColor: 'transparent',
  borderRadius: 1,
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
}

function resolveThemeColor(theme: Theme, colorPath: string): string {
  if (colorPath.startsWith('#') || colorPath.startsWith('rgb')) {
    return colorPath
  }

  const parts = colorPath.split('.')
  if (parts.length === 2) {
    const [palette, shade] = parts
    if (palette && shade) {
      const paletteObj = theme.palette[palette as keyof typeof theme.palette]
      if (paletteObj && typeof paletteObj === 'object' && shade in paletteObj) {
        const value = (paletteObj as unknown as Record<string, unknown>)[shade]
        if (typeof value === 'string') return value
      }
    }
  }

  return colorPath
}

const createProgressBarSx =
  (remainingPercent: number, colorPath: string, side: 'left' | 'right'): SxProps<Theme> =>
  (theme) => {
    const resolved = resolveThemeColor(theme, colorPath)
    return {
      position: 'absolute',
      [side]: 0,
      top: 0,
      width: `${remainingPercent / 2}%`,
      height: '100%',
      backgroundColor: resolved,
      borderRadius: side === 'left' ? '1px 0 0 1px' : '0 1px 1px 0',
    }
  }

const createButtonSx =
  (colorPath: string, activeColorPath?: string): SxProps<Theme> =>
  (theme) => {
    const resolved = resolveThemeColor(theme, colorPath)
    const restColor = activeColorPath
      ? resolveThemeColor(theme, activeColorPath)
      : theme.palette.text.secondary
    return {
      color: restColor,
      transition: 'color 0.2s ease',
      '&:hover': {
        backgroundColor: 'transparent',
        color: resolved,
      },
      '&.Mui-disabled': {
        color: 'action.disabled',
      },
    }
  }
